'use client'

import type { CSSProperties, FocusEvent, ReactNode } from 'react'
import { cx } from './utils/cx'

interface FieldProps {
  label?: ReactNode
  hint?: ReactNode
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  type?: string
  multiline?: boolean
  rows?: number
  style?: CSSProperties
  className?: string
  autoComplete?: string
  name?: string
  id?: string
  disabled?: boolean
  readOnly?: boolean
  onFocus?: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
  spellCheck?: boolean
  ariaDescribedBy?: string
  ariaInvalid?: boolean
  [dataAttr: `data-${string}`]: string | undefined
}

export function Field({
  label,
  hint,
  value,
  onChange,
  placeholder,
  type = 'text',
  multiline,
  rows = 3,
  style,
  className,
  autoComplete,
  name,
  id,
  disabled,
  readOnly,
  onFocus,
  inputMode,
  spellCheck,
  ariaDescribedBy,
  ariaInvalid,
  ...rest
}: FieldProps) {
  const dataProps = Object.fromEntries(Object.entries(rest).filter(([k]) => k.startsWith('data-')))
  return (
    <div className={cx('st-field', className)} style={style}>
      {(label || hint) && (
        <label htmlFor={id}>
          <span>{label}</span>
          {hint && <span className="st-field-hint">{hint}</span>}
        </label>
      )}
      {multiline ? (
        <textarea
          id={id}
          name={name}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          autoComplete={autoComplete}
          readOnly={readOnly}
          onFocus={onFocus}
          inputMode={inputMode}
          spellCheck={spellCheck}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          {...dataProps}
        />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={value ?? ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          readOnly={readOnly}
          onFocus={onFocus}
          inputMode={inputMode}
          spellCheck={spellCheck}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          {...dataProps}
        />
      )}
    </div>
  )
}
